import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button, Link, MessageBar, MessageBarBody, Spinner, Subtitle2, Card, Text, makeStyles, tokens } from '@fluentui/react-components';
import { PageHeader, StatCard } from '../common';
import { api } from '../../api/client';
import type { DeploymentSession, GuardianInstallation } from '../../api/types';

const useStyles = makeStyles({
  row: { display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalL, marginBottom: tokens.spacingVerticalL },
  list: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
});

const DAY = 24 * 3600 * 1000;

/**
 * What is happening right now — counts derived only from real records: Guardian installations
 * (GET /api/installations) and deployment sessions (GET /api/sessions). No sample data.
 */
export function DashboardPage() {
  const styles = useStyles();
  const navigate = useNavigate();
  const installs = useQuery({ queryKey: ['installations'], queryFn: () => api<GuardianInstallation[]>('installations') });
  const sessions = useQuery({ queryKey: ['sessions'], queryFn: () => api<DeploymentSession[]>('sessions') });

  if (installs.isLoading || sessions.isLoading) return <Spinner label="Loading…" />;
  const err = installs.error || sessions.error;

  const i = installs.data ?? [];
  const s = sessions.data ?? [];
  const now = Date.now();
  const count = (state: string) => i.filter((x) => x.State === state).length;
  const completedThisWeek = s.filter((x) => x.status === 'completed' && now - Date.parse(x.updatedAt) < 7 * DAY).length;
  const active = s.filter((x) => x.status === 'active');
  const failedSessions = active.filter((x) => Object.values(x.steps).some((st) => st.status === 'failed'));
  const durations = s.filter((x) => x.status === 'completed').map((x) => Date.parse(x.updatedAt) - Date.parse(x.createdAt));
  const avgMinutes = durations.length ? Math.round(durations.reduce((a, b) => a + b, 0) / durations.length / 60000) : null;

  return (
    <div>
      <PageHeader title="Dashboard" description="Deployments across every customer tenant, from FirstIQ Guardian and Deployment Manager records."
        actions={<Button appearance="primary" onClick={() => navigate('/deploy')}>New deployment</Button>} />
      {err && <MessageBar intent="error"><MessageBarBody>{(err as Error).message}</MessageBarBody></MessageBar>}

      <div className={styles.row}>
        <StatCard title="Ready to deploy" value={count('ReadyToDeploy')} />
        <StatCard title="In progress" value={active.length} />
        <StatCard title="Awaiting customer Azure" value={count('AwaitingCustomerAzure')} />
        <StatCard title="Failed" value={count('Failed') + failedSessions.length} />
      </div>
      <div className={styles.row}>
        <StatCard title="Live / in use" value={count('Live') + count('InUse')} />
        <StatCard title="Completed this week" value={completedThisWeek} />
        <StatCard title="Avg. time to deploy" value={avgMinutes === null ? '—' : `${avgMinutes} min`} />
        <StatCard title="Customers" value={new Set(i.map((x) => x.tenantId)).size} />
      </div>

      <Card>
        <Subtitle2>Needs attention</Subtitle2>
        <div className={styles.list}>
          {failedSessions.length === 0 && count('Failed') === 0 && <Text>Nothing has failed.</Text>}
          {failedSessions.map((x) => {
            const step = Object.entries(x.steps).find(([, st]) => st.status === 'failed');
            return (
              <Text key={x.id}>
                <Link onClick={() => navigate(`/deploy/${x.id}`)}>{x.displayName} — {x.customerName || x.tenantId}</Link>
                {step ? `: ${step[0]} — ${step[1].message}` : ''}
              </Text>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
